import moment from 'moment';
import { useDateFormat } from '../hooks';

/**
 * This function takes an array of field to perform case insensitive filters within lists
 * @param {string} values - Values can be passed as a string "name" or as an array ["name", "start_date"]
 * @returns {boolean} The search's result
 */
export const matchSearch = (values, search) => {
    if (!Array.isArray(values)) {
        values = [values];
    }
    // Escape special regex characters
    const safeSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const reg = new RegExp(safeSearch, 'gi');
    return values.some(value => reg.test(value));
};

/**
 * This function returns a list of unique ids to be used for checkboxes selections
 * @param {array} source - Selection source
 * @param {string} value - Value to be checked
 * @returns {array} Unique selection list
 */
export const uniqueSelection = (source, value) => {
    let selection = new Set(source);
    if (!selection.has(value)) {
        selection.add(value);
    } else {
        selection.delete(value);
    }
    return [...selection];
};

/**
 * This function performs a global selection on a given list
 * @param {array} source - A list of objects
 * @param {array} ids - A list of ids for comparison
 * @returns {array} Either a full selection, or none
 */
export const multipleSelection = (source, ids) => {
    const allSelected = ids.every(id => source.includes(id));
    return allSelected ? [] : ids;
};

/**
 * This function performs a unique or global selection on a given list, depending on parameters
 * @param {array} source - A list of objects
 * @param {array} ids - A list of ids for comparison
 * @param {string} value - Value to be checked
 * @returns {array} If value is passed, unique selection list. Else, either a full selection, or none.
 */
export const listSelection = (source, ids, value) => {
    if (!source) {
        throw new Error('[listSelection] source is mandatory');
    }
    if (!value) {
        return multipleSelection(source, ids);
    }
    return uniqueSelection(source, value);
};

export const resolve = (value, fallback) => {
    if (!value) {
        return fallback;
    }
    return value;
};

/**
 * This function allows to dymalically edit list using front toggles, it can either add items , or delete at specific index
 * @param {array} source - A list of objects
 * @param {object} additions - An element to be pushed to the source list
 * @param {number} value - Value to switch from push to splice (usually null/index)
 * @returns {array} Returns edited source list.
 */
export const pushOrSplice = (source, additions, value) => {
    if (!source) {
        throw new Error('[pushOrSplice] source is mandatory');
    }
    const addition = { ...additions };
    let new_source = [...source];

    if (!value && value !== 0) {
        return [...new_source, addition];
    } else {
        new_source.splice(value, 1);
        return new_source;
    }
};

export const getCount = (request, header) => request?.__meta?.headers.get(header);

export const requestCount = (loading, request) => {
    if (loading) {
        return '--';
    }
    return Number.parseInt(getCount(request, 'total-count')) || 0;
};

export const isEmpty = obj => Object.entries(obj).length === 0;

export const isEmail = value => {
    if (!value) {
        return false;
    }
    const rule = new RegExp(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,}$/);
    return rule.test(value);
};

function processFilters(filters, callback) {
    Object.entries(filters).forEach(([key, value]) => {
        callback(key, value);
    });
}

export function handleWebhookSearchQuery(spec, filters, truncate = false) {
    let query = {};

    processFilters(filters, (key, value) => {
        if (truncate && !spec.fields?.includes(key)) {
            return;
        } else if (value instanceof Set) {
            value = Array.from(value).join(',');
        } else if (spec.date?.includes(key) && value instanceof Object && !Array.isArray(value)) {
            Object.entries(value).forEach(([_key, _value]) => {
                if (_value) {
                    query[`${key}_${_key}`] = moment(_value)
                        .set({ second: 0, millisecond: 0 })
                        .toISOString();
                }
            });
            return;
        }
        query[key] = value;
    });

    return query;
}

export function handleSearchQuery(spec, filters, truncate = false) {
    let query = {};
    Object.entries(filters).forEach(([key, value]) => {
        if (truncate && !spec.fields?.includes(key)) {
            // Ignore key that are not in the spec fields in case we don't need all the object
            // For example when we want to call API we don't want all the filters,
            // just those who are useful in this case
            return;
        }

        if (value instanceof Set) {
            query[key] = Array.from(value).join(',');
            return;
        }

        // case Filter date
        if (spec.date?.includes(key) && value instanceof Object && !Array.isArray(value)) {
            Object.entries(value).forEach(([_key, _value]) => {
                if (_value) {
                    query[`${key}_${_key}`] = moment(_value)
                        .set({ second: 0, millisecond: 0 })
                        .toISOString();
                }
            });
            return;
        }

        query[key] = value;
    });

    return query;
}

export const capitalizeFirstLetter = string => {
    if (!string) {
        return '';
    }

    return `${string.charAt(0).toUpperCase()}${string.slice(1).toLowerCase()}`;
};

export const validate = value => {
    return value || null;
};

export const checkIsValidJSON = value => {
    try {
        JSON.parse(value);
        return true;
    } catch {
        return false;
    }
};

/**
 * Format a date with time
 * @param {string} date - Date to format
 * @param {string} fallback - Fallback value
 * @returns {string} Formatted date
 */
export const formatDateWithTime = (date, fallback = '-') => {
    if (!date) return fallback;

    try {
        const { properTime, properDate } = useDateFormat({ date });
        return `${properDate} - ${properTime}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return fallback;
    }
};

export const formatDateOnly = (date, separator = '/') => {
    if (!date) return '-';
    date = new Date(date);
    return `${String(date.getDate()).padStart(2, '0')}${separator}${String(date.getMonth() + 1).padStart(2, '0')}${separator}${date.getFullYear()}`;
};

export const formatHourOnly = date => {
    if (!date) return '-';
    date = new Date(date);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const filterEmptyValues = obj => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (key && value) {
            acc[key] = value;
        }
        return acc;
    }, {});
};

// Set timezone on date (for frontend displaying)
export const setTimezoneToDate = date => {
    const copy = new Date(date);
    copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
    return copy;
};

// Remove timezone on date (before request)
export const removeTimezoneOfDate = date => {
    const copy = new Date(date);
    copy.setMinutes(copy.getMinutes() + copy.getTimezoneOffset());
    return copy;
};

const ORIGINS = ['http://localhost', 'https://admin.uat'];

export const locationOrigin = () => {
    var window_origin = window.location.origin;
    if (ORIGINS.some(orig => window_origin.startsWith(orig))) {
        // If we are on localhost or uat, it's unsure the product is hosted so let's redirect to staging.
        window_origin = `https://admin.weezevent.be`;
    }
    return window_origin;
};

export const filterRoles = (role, roles, permissions) => {
    // Get all user permissions slugs according to the selected context value
    const userCapacities = new Set(
        permissions._roles
            .filter(
                r => (r.context.level !== 0 && r.context.level - 1 < role.level) || (r.context.level - 1 === role.level && r.context.value === String(role.context[role.level]))
            )
            .flatMap(r => r.role.capacities.map(c => c.slug))
    );
    return roles.filter(r => {
        const capacities = r.object.capacities.map(c => c.slug);
        return capacities.every(slug => userCapacities.has(slug));
    });
};

export const checkIsDomainName = name => {
    const regexDomainName = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

    if (!regexDomainName.test(name)) {
        throw new Error(`${name} is not a valid domain name`);
    }
    return name;
};

/**
 * Converts a raw string of DNS records into an array of structured objects.
 * Each line in the input should be in the format: "<type> <name> <value>".
 * Values can contain spaces.
 *
 * @param {string} raw - The raw DNS records string
 * @returns {Array<{type: string, name: string, value: string}>}
 *          An array of objects with `type`, `name`, and `value` properties
 */
export const formatAttributes = raw =>
    (raw || '')
        .trim()
        .split('\n')
        .map(line => line.trim().split(/\s+/))
        .filter(parts => parts.length >= 3)
        .map(([type, name, ...rest]) => ({ type, name, value: rest.join(' ') }));
